import { select } from '@angular-redux/store';
import {
  AbstractType,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  InjectionToken,
  Injector,
  NgZone,
  OnDestroy,
  OnInit,
  TemplateRef,
  Type,
  ViewChild,
} from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { DndMode } from '@app/components/xux-dnd/enums';
import {
  DashboardModuleKeyNameMapping,
  DndColListConfig,
  DndConfig,
  IGridItemConfig,
  IItemToPush,
  IModule,
} from '@app/components/xux-dnd/types';
import { XuxDndListComponent } from '@app/components/xux-dnd/xux-dnd-list/xux-dnd-list.component';
import { lazyInject } from '@app/containers/base/decorators';
import { EDITOR_MESSAGES } from '@app/shared/constants/editor-messages';
import { IComponentCanDeactivate } from '@app/shared/helpers/edit-guard/edit-guard.helper';
import {
  BehaviorSubject,
  Observable,
  rx,
  Subject,
  Subscription,
} from '@app/shared/rx/rx-wrapper';
import {
  NumberFormatService,
  PermissionService,
  StateService,
  STATE_KEYS,
  ToolsService,
  SitesService,
  EventsService,
  XprtDataService,
} from '@app/shared/services';
import {
  IUserConfig,
  USER_CONFIG_MODULE_NAME,
  USER_CONFIG_MODULE_TYPE,
  WIDGET_COMPONENT,
  GRID_ROW_SELECTION,
  IModalDialogOptions,
  IModalDialogResult,
  DashboardModules,
  ISite,
  IFilterQuery,
  IProcessedEvent,
  IEventStatus,
  DATA_SOURCE_VALUE,
  DATE_FILTER_TYPES,
  IFormAction,
  XUX_GRID_ACTIONS,
  ENTITY_TYPES,
  GridsterConfig,
  GridsterItem,
  GridsterItemComponent,
  GridsterPush,
  CompactType,
  DisplayGrid,
  GridType,
  NotificationType,
  INotificationBoxItem,
  NotificationContentType,
  INotificationHTML,
  IBaseEntityType,
  DashboardWidgetModules,
} from '@app/shared/types';
import { APIActions, EventsActions, UiActions } from '@app/store/actions';
import {
  BTN_CANCEL,
  BTN_DONE,
  BTN_YES,
} from '@app/text-content/button-templates';
import {
  EVENT_SELECTOR_SELECT,
  NO_THEME_CLASS,
  SITE_SELECTOR_SELECT,
  WIDGET_FAMILY,
  XUX_DASHBOARD_THEME_CLASS,
} from '@config/global-constants';
import {
  chain,
  filter,
  find,
  findIndex,
  forEach,
  get,
  head,
  isEmpty,
  isEqual,
  isFunction,
  isNil,
  keys,
  map,
  partition,
  reduce,
  size,
  some,
  split,
  values,
  negate,
  set,
  pick,
  difference,
  merge,
  forEachRight,
  matchesProperty,
  every,
  eq,
  groupBy,
  orderBy,
  cloneDeep,
} from 'lodash';
import {
  defaultItemConfig,
  getTotalModules,
} from './landing-page-modules.constants';

import { first } from 'rxjs/operators';
import { IDatepickerValue } from '@geniuspos/ui-shared/modules/xdm-shared-components/components/xux-datepicker/types';
import { XuxDndListService } from '@app/components/xux-dnd/services/xux-dnd-list.service';
import {
  isDndItemHidden,
  isDndItemNoPermission,
} from '@app/components/xux-dnd/utils/xux-dnd-item.utils';
import { GLOBAL_DND_COMPLICATE_COMP_SHOW_LOADER_OBS } from '@app/components/xux-dnd/constants/xnx-dnd.subjects';
import { combineLatest } from 'rxjs';
import { getSelectedItemsDescriptionText } from '@app/shared/utils/common-utils';
import { toMoment, generateId } from '@shared/utils';
import { XuxDndItemComponent } from '@app/components/xux-dnd/xux-dnd-item/xux-dnd-item.component';
import { XUXDrawerSidebarService } from '@app/xenial-ux/services';
import { ToasterService } from 'angular2-toaster';
import { I18n } from '@app/i18n/strings-keys';
import { XuxDndCacheService } from '@app/shared/services/xux-dnd-cache/xux-dnd-cache.service';
import { XVII18nService } from '@app/i18n/services';

@Component({
  selector: 'xvi-landing-page',
  templateUrl: './landing-page.component.html',
  styleUrls: ['./landing-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LandingPageComponent
  implements OnInit, OnDestroy, IComponentCanDeactivate
{
  @ViewChild('dndList', { static: false })
  public dndList: XuxDndListComponent;

  @ViewChild('XuxDndItemTemplate', { static: true })
  public XuxDndItemTemplate: TemplateRef<XuxDndItemComponent>;

  @lazyInject(ChangeDetectorRef) public cdr: ChangeDetectorRef;
  @lazyInject(PermissionService) public permissionService: PermissionService;
  @lazyInject(ToolsService) public toolsService: ToolsService;
  @lazyInject(StateService) public stateService: StateService;
  @lazyInject(APIActions) public apiActions: APIActions;
  @lazyInject(UiActions) public uiActions: UiActions;
  @lazyInject(NumberFormatService)
  public numberFormatService: NumberFormatService;
  @lazyInject(SitesService) public sitesService: SitesService;
  @lazyInject(EventsService) public eventsService: EventsService;
  @lazyInject(EventsActions) public eventsActions: EventsActions;
  @lazyInject(XuxDndListService) public xuxDndListService: XuxDndListService;
  @lazyInject(XprtDataService) public xprtDataService: XprtDataService;
  @lazyInject(XUXDrawerSidebarService)
  public drawerSidebarService: XUXDrawerSidebarService;
  @lazyInject(XuxDndCacheService)
  public xuxDndCacheService: XuxDndCacheService;
  @lazyInject(XVII18nService)
  public i18nService: XVII18nService;

  public isEditMode$: BehaviorSubject<boolean> = new BehaviorSubject(false);

  @select(STATE_KEYS.USER_CONFIG)
  public userConfig$: Observable<any>;
  public subscriptions: Subscription[] = [];

  public cancelSubscription$: Subject<void> = new Subject();

  public transferCanView: boolean = false;
  public orderCanView: boolean = false;
  public eventsCanView: boolean = false;

  public defaultItemConfig: IGridItemConfig[];

  public listColConfig: DndColListConfig[] = [];

  public dndListColConfig: DndColListConfig[] = [];

  public dndConfig: DndConfig = {};

  public modulesChanged: boolean = false;
  public modulesChanged$: BehaviorSubject<boolean> = new BehaviorSubject(false);

  public siteList$: BehaviorSubject<ISite[]> = new BehaviorSubject(null);
  public events$: BehaviorSubject<IProcessedEvent[]> = new BehaviorSubject(
    null,
  );
  public events: IProcessedEvent[];
  public defaultEvents: IBaseEntityType[] = [];
  public defaultSource: DATA_SOURCE_VALUE = DATA_SOURCE_VALUE.All_Sources;

  public defaultSiteIds = [];
  public gridsterComponent = null;
  public defaultMinCols: number = 2;

  public defaultDate: IDatepickerValue = {
    type: DATE_FILTER_TYPES.CUSTOM,
    value: {
      // eslint-disable-next-line newline-per-chained-call
      dateStart: toMoment().utc().subtract(6, 'days').toDate(),
      dateEnd: toMoment().utc().toDate(),
    },
  };

  public layoutOptions: GridsterConfig = {
    gridType: GridType.VerticalFixed,
    displayGrid: DisplayGrid.None,
    margin: 20,
    pushItems: true,
    pushDirections: { north: true, east: false, south: true, west: false },
    compactType: CompactType.None,
    disableWindowResize: false,
    scrollToNewItems: false,
    disableWarnings: false,
    ignoreMarginInRow: true,
    outerMarginTop: 40,
    outerMarginRight: 40,
    outerMarginBottom: 40,
    outerMarginLeft: 40,
    outerMargin: true,
    maxCols: this.defaultMinCols,
    fixedRowHeight: 20,
    // Set to 100000, to avoid left/right shifts caused by the default maxRows=100 (which prevents modules from being placed in a single column when fixedRowHeight:20 and all modules need to be in one column).
    maxRows: 100000,
    swap: true,
    swapWhileDragging: true,
    alwaysShow: true,
    autoPosition: true,
    initCallback: (gridsterComponent) => {
      this.gridsterComponent = gridsterComponent;
    },
    itemChangeCallback: () => {
      this.adjustItemPosition();
    },
    draggable: {
      enabled: false,
      stop: () => {
        this.modulesChanged = true;
        this.adjustItemPosition();
      },
    },
  };
  public dashboardCells: IGridItemConfig[];
  public defaultItemKeys: string[] = [];
  public isNewWidgetReminderInitialized: boolean = false;
  public newWidgets: string[] = [];
  public notificationId: string;

  public totalModules: IModule;
  private subscription: Subscription[] = [];

  public itemToPush: IItemToPush = {
    events: null,
    transfers: null,
    purchase_orders: null,
    inventory_value: null,
    stand_worksheet: null,
  };

  public constructor(
    public injector: Injector,
    private zone: NgZone,
    public toasterService: ToasterService,
  ) {
    this.isEditMode$.next(false);
    this.uiActions.setUiTheme(XUX_DASHBOARD_THEME_CLASS);
    this.defaultItemConfig = defaultItemConfig(this);
    this.defaultItemKeys = map(this.defaultItemConfig, 'key');

    this.totalModules = getTotalModules(this);

    this.dndConfig = {
      ...this.dndConfig,
      cacheFormValue: true,
      modeObs: this.isEditMode$.pipe(
        rx.map((isEditMode) => (isEditMode ? DndMode.DRAG : DndMode.IDLE)),
      ),
    };

    this.buildBreadcrumbButtons();
  }

  public async ngOnInit(): Promise<void> {
    this.uiActions.enableLoader();
    const haveEventView: boolean =
      this.permissionService.hasPortalPermissions('Events.View');
    const haveSitesView: boolean =
      this.permissionService.hasPortalPermissions('Sites.Items.View');
    const haveTransferView: boolean = this.permissionService.canView(
      ENTITY_TYPES.TRANSFER,
    );
    const haveStandWorksheetView: boolean = this.permissionService.canView(
      ENTITY_TYPES.STAND_WORKSHEET,
    );
    const canViewTransferModule: boolean =
      haveTransferView && haveEventView && haveSitesView;
    const canViewStandWorksheetModule: boolean =
      haveStandWorksheetView && haveEventView;
    if (canViewTransferModule || canViewStandWorksheetModule) {
      if (canViewTransferModule) {
        const sites = await this.fetchAllSites({});
        this.defaultSiteIds = map(sites, 'id');
        this.siteList$.next(sites);
      }
      const params = {
        filter: `status eq in(${IEventStatus.Open},${IEventStatus.Closed})`,
        orderby: `status desc,event_days/start_date_time desc`,
        select: split(EVENT_SELECTOR_SELECT),
      };

      const events = await this.eventsService
        .fetchList(params)
        .pipe(
          first(),
          rx.map((res) => get(res, 'items')),
        )
        .toPromise();
      const [openedEvents, closedEvents] = partition(events, (event) =>
        isEqual(get(event, 'status'), IEventStatus.Open),
      );
      this.events$.next(events as IProcessedEvent[]);

      if (!isEmpty(openedEvents)) {
        this.defaultEvents = openedEvents;
      } else if (!isEmpty(closedEvents)) {
        this.defaultEvents = [head(closedEvents)];
      }
    }

    this.buildModules();
    this.subscription.push(
      this.drawerSidebarService.slideState$.subscribe(() => {
        this.layoutOptions?.api?.resize();
        this.cdr.markForCheck();
      }),
    );
  }

  public ngOnDestroy(): void {
    this.subscription.forEach((sub) => sub.unsubscribe());
    this.uiActions.setUiTheme(NO_THEME_CLASS);
    this.cancelSubscription$.next();
    this.exitEditMode();
    this.eventsActions.saveEvents([]);
    this.uiActions.removeNotificationsOnPageLeave();
  }

  public inject<T>(
    token: Type<T> | InjectionToken<T> | AbstractType<T>,
    notFoundValue?: T,
  ): T {
    if (isFunction(this.injector.get)) {
      return this.injector?.get(token, notFoundValue);
    }

    return notFoundValue;
  }

  public setModulesChange(changed: boolean = false): void {
    this.modulesChanged = changed;
    this.modulesChanged$.next(changed);
  }

  public exitEditMode(): void {
    this.setModulesChange(false);
    this.layoutOptions.draggable.enabled = false;
    // Single-column modules auto-expand to full page width after exiting edit mode.
    this.layoutOptions.minCols = 1;
    if (!!this.layoutOptions && !!this.layoutOptions.api) {
      this.layoutOptions.api.optionsChanged();
    }
    this.isEditMode$.next(false);
  }

  public buildBreadcrumbButtons(): void {
    this.uiActions.updateBreadcrumbButtons([
      {
        caption: I18n.Common.EditMode,
        class: 'btn-primary',
        hiddenObs: this.isEditMode$,
        disabledObs: GLOBAL_DND_COMPLICATE_COMP_SHOW_LOADER_OBS.pipe(
          rx.map((complicateShowLoader) =>
            some(values(complicateShowLoader), (showLoader) => showLoader),
          ),
          rx.distinctUntilChanged(),
        ),
        click: () => {
          this.isEditMode$.next(true);
          this.layoutOptions.draggable.enabled = true;
          this.layoutOptions.minCols = this.defaultMinCols;
          if (!!this.layoutOptions && !!this.layoutOptions.api) {
            this.layoutOptions.api.optionsChanged();
          }
        },
      },
      {
        caption: this.i18nService.instant(I18n.Common.TileSelector),
        hiddenObs: this.isEditMode$.pipe(rx.map((isEditMode) => !isEditMode)),
        class: 'mr-10',
        click: () => {
          this.manageModal();
        },
      },
      {
        caption: this.i18nService.instant(I18n.Common.Save),
        class: 'btn-primary',
        hiddenObs: this.isEditMode$.pipe(rx.map((isEditMode) => !isEditMode)),
        splitDropdown: true,
        click: async () => {
          if (!this.modulesChanged) {
            this.exitEditMode();

            return;
          }

          this.uiActions.enableLoader();

          // build config
          const hiddenModules = chain(this.defaultItemKeys)
            .difference(map(this.dashboardCells, 'key'))
            .map((key) => ({
              key,
            }))
            .valueOf();

          this.changeItemX();

          const showingColList = map(this.dashboardCells, (cell) =>
            pick(cell, ['x', 'y', 'cols', 'rows', 'key']),
          );

          const moduleConfig = <DashboardModules>{
            showingColList,
            hiddenModules,
          };

          try {
            await this.toolsService
              .saveUserConfig(
                USER_CONFIG_MODULE_TYPE.DASHBOARD,
                USER_CONFIG_MODULE_NAME.DASHBOARD_MODULES,
                moduleConfig,
              )
              .toPromise();

            this.uiActions.updateUserConfig([
              {
                module_config: moduleConfig,
                module_type: USER_CONFIG_MODULE_TYPE.DASHBOARD,
                module_name: USER_CONFIG_MODULE_NAME.DASHBOARD_MODULES,
              },
            ]);
            if (
              this.isNewWidgetReminderInitialized &&
              !isEmpty(this.newWidgets)
            ) {
              await this.saveWidgetConfig();
            }

            this.exitEditMode();
          } catch (error) {
          } finally {
            this.uiActions.disableLoader();
          }
        },
        childItems: [
          {
            caption: this.i18nService.instant(I18n.Common.Discard),
            click: async () => {
              let discardChanges: boolean = true;
              if (this.modulesChanged) {
                const options: IModalDialogOptions = <IModalDialogOptions>{
                  title: EDITOR_MESSAGES.customTitle(null),
                  text: this.i18nService.instant(
                    I18n.InfoMessages.UnsavedTileExit,
                  ),
                  buttons: [BTN_YES, BTN_CANCEL],
                };

                const { confirmed }: IModalDialogResult =
                  await this.uiActions.confirm(options);

                discardChanges = confirmed;
              }
              if (discardChanges) {
                if (this.modulesChanged) {
                  this.buildModules();
                }

                this.exitEditMode();
              }
            },
          },
        ],
      },
    ]);
  }

  public buildModules(): void {
    this.userConfig$
      .pipe(
        rx.takeUntil(this.cancelSubscription$),
        rx.filter(negate(isNil)),
        rx.take(1),
      )
      .subscribe((data: IUserConfig[]) => {
        const dashboardUserConfigFromApi = find(data, <Partial<IUserConfig>>{
          module_name: USER_CONFIG_MODULE_NAME.DASHBOARD_MODULES,
          module_type: USER_CONFIG_MODULE_TYPE.DASHBOARD,
        });
        const dashboardWidgetsFromApi = find(data, <Partial<IUserConfig>>{
          module_name: USER_CONFIG_MODULE_NAME.DASHBOARD_WIDGETS,
          module_type: USER_CONFIG_MODULE_TYPE.DASHBOARD,
        });

        const apiDashboardModules =
          dashboardUserConfigFromApi?.module_config as DashboardModules;
        const apiDashboardWidgetsModules =
          dashboardWidgetsFromApi?.module_config as DashboardWidgetModules;

        let dashboardModules: IGridItemConfig[] = [];
        if (isEmpty(apiDashboardModules)) {
          dashboardModules = merge([], this.defaultItemConfig);
        } else {
          if (!isEmpty(apiDashboardModules.showingColList)) {
            dashboardModules = [...apiDashboardModules.showingColList];
          }
        }
        // After the new widget is released, it will reminder the user
        const allWidgetObjects = Object.values(this.totalModules).reduce(
          (acc, curr) => {
            acc[curr.key] = {
              title: curr.title,
              permissionChecker: curr.permissionChecker,
            };

            return acc;
          },
          {},
        );
        if (!this.isNewWidgetReminderInitialized) {
          // If the new widget is not confirmed, it will show a notification
          const confirmedList =
            apiDashboardWidgetsModules.confirmedWidgets ?? [];
          this.newWidgets = Object.entries(allWidgetObjects)
            .filter(([key]) => {
              return (
                !confirmedList.includes(key) &&
                allWidgetObjects[key].permissionChecker(this.permissionService)
              );
            })
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            .map(([key, value]) => (value as any).title);
          if (!isEmpty(this.newWidgets)) {
            const notificationConfig: INotificationBoxItem =
              this.getNotificationConfigForMappings();
            this.notificationId = notificationConfig.id;
            this.uiActions.addNotification(notificationConfig);
          }
          this.isNewWidgetReminderInitialized = true;
        }
        this.dashboardCells = filter(
          map(dashboardModules, (itemConfig) => {
            return {
              ...itemConfig,
              template: this.getWidgetTemplate(),
              itemConfig: get(this.totalModules, itemConfig.key),
              initCallback: this.initItem.bind(this),
              class: get(
                find(this.defaultItemConfig, { key: itemConfig.key }),
                'class',
              ),
            };
          }),
          (itemConfig) =>
            !isDndItemHidden(itemConfig.itemConfig, {
              permissionService: this.permissionService,
            }),
        );
        this.uiActions.disableLoader();
        this.cdr.markForCheck();
      });
  }

  public initItem(
    item: GridsterItem,
    itemComponent: GridsterItemComponent,
  ): void {
    set(this.itemToPush, item.key, itemComponent);
  }

  public changeItemX(): void {
    if (!every(this.dashboardCells, matchesProperty('x', 1))) {
      return;
    }
    const cellKeys: string[] = [];
    forEach(this.dashboardCells, (cell) => {
      cellKeys.push(cell.key);
    });

    forEach(cellKeys, (key) => {
      this.changeItemPosition(key);
    });
  }

  public changeItemY(): void {
    const list = groupBy(
      orderBy(cloneDeep(this.dashboardCells), ['x', 'y']),
      'x',
    );
    forEach(list, (group) => {
      let baseY = 0;
      forEach(group, (cell: any) => {
        const cellConfig = find(this.defaultItemConfig, ['key', cell.key]);
        this.changeItemPosition(cell.key, baseY, cellConfig);
        baseY = baseY + cellConfig.rows;
      });
    });
  }

  public changeItemPosition(
    key: string,
    y?: number,
    cellConfig?: IGridItemConfig,
  ): void {
    const cellComponent = this.itemToPush[key];
    const push = new GridsterPush(cellComponent);
    if (!isNil(y)) {
      cellComponent.$item.y = y;
      cellComponent.$item.rows = cellConfig.rows;
      cellComponent.$item.cols = cellConfig.cols;
    } else {
      cellComponent.$item.x = 0;
    }
    push.checkPushBack(); // check for items can restore to original position
    push.setPushedItems(); // save the items pushed
    cellComponent.setSize();
    cellComponent.checkItemChanges(cellComponent.$item, cellComponent.item);
    push.destroy(); // destroy push instance

    this.zone.run(() => {
      this.gridsterComponent.updateGrid();
    });
  }

  public async manageModal() {
    const validModules = reduce(
      filter(
        this.totalModules,
        (module) =>
          !isDndItemNoPermission(module, {
            permissionService: this.permissionService,
          }),
      ),
      (list, curModule) => {
        return list.concat({
          ...curModule,
          module_name: this.i18nService.instant(
            DashboardModuleKeyNameMapping[curModule.key],
          ),
          hidden: false,
        });
      },
      [],
    );

    const listItemCount$ = new BehaviorSubject<number>(
      keys(validModules)?.length ?? 0,
    );

    const selectItemCount$ = new BehaviorSubject<number>(0);

    const modalOptions: IModalDialogOptions = {
      title: this.i18nService.instant(I18n.Common.TileSelector),
      subTitleObs: combineLatest([listItemCount$, selectItemCount$]).pipe(
        rx.map(([totalCount, selectCount]) =>
          getSelectedItemsDescriptionText(selectCount, totalCount, {
            i18nService: this.i18nService,
          }),
        ),
      ),
      buttons: [BTN_DONE, BTN_CANCEL],
      schema: {
        type: 'object',
        properties: {
          list: {
            type: 'array',
          },
        },
      },
      value: {
        list: this.dashboardCells,
      },
      formRules: {
        fields: {
          list: {
            title: null,
            ignoreAutoTitleWhenTranslate: true,
            widgetFamily: WIDGET_FAMILY.XUX,
            widget: WIDGET_COMPONENT.grid_list,
            widgetData: {
              returnFullData: true,
              userGridOptions: {
                showSearch: false,
                rowSelection: GRID_ROW_SELECTION.MULTIPLE,
                suppressRowClickSelection: true,
              },
              gridHeight: '270px',
              hideActionPanel: true,
              valueField: 'key',
              selectOptions: validModules,
              columns: [
                {
                  title: this.i18nService.instant(I18n.Common.TileName),
                  path: 'module_name',
                  defaultView: true,
                  sortable: false,
                },
              ],
            },
          },
        },
      },
      onFormAction: (formAction: IFormAction) => {
        if (isEqual(XUX_GRID_ACTIONS.ITEMS_ARRAY_CHANGED, formAction.type)) {
          listItemCount$.next(size(formAction.payload?.rowData ?? []));
        }

        if (isEqual(XUX_GRID_ACTIONS.ROW_SELECTION_CHANGED, formAction.type)) {
          const selectRows = formAction.payload?.rowData ?? [];
          selectItemCount$.next(size(selectRows));
        }
      },
    };

    const result = await this.uiActions.promptFormModal(modalOptions);

    if (!result.confirmed) {
      return;
    }

    const previousListKeys = map(this.dashboardCells, 'key');
    const newListKeys = map(result.value?.list, 'key');

    if (isEqual(previousListKeys, newListKeys)) {
      return;
    }

    this.setModulesChange(true);

    const deleteKeys: string[] = difference(previousListKeys, newListKeys);

    const addKeys: string[] = difference(newListKeys, previousListKeys);

    forEach(deleteKeys, (key) => {
      // remove dnd ref values
      this.xuxDndListService.destroyComponentByKey(
        XuxDndListService.buildCompMapKey(key),
      );

      this.dashboardCells.splice(
        findIndex(this.dashboardCells, (cell) => cell.key === key),
        1,
      );
    });

    let addItemConfig = null;
    if (
      isEmpty(previousListKeys) &&
      eq(size(newListKeys), size(this.defaultItemKeys))
    ) {
      this.dashboardCells = map(this.defaultItemConfig, (config) => {
        return {
          ...config,
          template: this.getWidgetTemplate(),
          initCallback: this.initItem.bind(this),
        };
      });
    } else {
      forEachRight(addKeys, (key) => {
        addItemConfig = find(this.defaultItemConfig, { key });
        this.dashboardCells.unshift({
          ...addItemConfig,
          x: 0,
          y: 0,
          template: this.getWidgetTemplate(),
          initCallback: this.initItem.bind(this),
        });
      });
    }
    setTimeout(() => {
      this.changeItemY();
    }, 100);
    this.cdr.markForCheck();
  }

  public async canDeactivate(
    _: IComponentCanDeactivate,
    __: ActivatedRouteSnapshot,
    ___: RouterStateSnapshot,
    ____?: RouterStateSnapshot,
  ): Promise<boolean> {
    if (!this.modulesChanged) {
      return true;
    }

    const options: IModalDialogOptions = <IModalDialogOptions>{
      title: EDITOR_MESSAGES.customTitle(null),
      text: this.i18nService.instant(I18n.InfoMessages.UnsavedTileExit),
      buttons: [BTN_YES, BTN_CANCEL],
    };

    const { confirmed }: IModalDialogResult =
      await this.uiActions.confirm(options);

    return confirmed;
  }

  public async fetchAllSites(query: IFilterQuery): Promise<ISite[]> {
    return this.xprtDataService
      .getAllSitesByFilter({
        filter: `is_active eq true`,
        select: SITE_SELECTOR_SELECT,
        include_inactive: false,
        filter_by_assigned_sites: false,
        ...query,
      })
      .pipe(rx.first())
      .toPromise();
  }

  private getWidgetTemplate(): TemplateRef<XuxDndItemComponent> {
    return this.XuxDndItemTemplate;
  }

  public removeItem(item: IGridItemConfig) {
    this.dashboardCells.splice(this.dashboardCells.indexOf(item), 1);
    this.setModulesChange(true);
  }

  public adjustItemPosition(): void {
    setTimeout(() => {
      this.changeItemY();
    }, 100);
  }

  public getNotificationConfigForMappings(): INotificationBoxItem {
    if (isEmpty(this.newWidgets)) {
      return;
    }
    // Generate a unique ID for the notification
    const id = generateId();
    const widgetTitle = `<div><span style="font-weight: bold">${this.i18nService.instant(
      I18n.Components.LandingPageComponent.NewWidgetAvailable,
      {
        widget:
          this.newWidgets.length > 1
            ? this.i18nService.instant(
                I18n.Components.LandingPageComponent.widgetsAre,
              )
            : this.i18nService.instant(
                I18n.Components.LandingPageComponent.widgetIs,
              ),
      },
    )}</span></div>`;
    const widgetAvailableContent = `<ul>
      ${this.newWidgets
        .map(
          (widgetName) =>
            `<li><span style="font-weight: bold">${widgetName}</span></li>`,
        )
        .join('')}
    </ul>`;
    const widgetFooter = `<div style="margin-top: -10px;">${this.i18nService.instant(
      I18n.Components.LandingPageComponent.ToCustomizeDashboard,
    )}<span style="font-weight: bold">${this.i18nService.instant(
      I18n.Components.LandingPageComponent.TileSelectorLocation,
    )}</span></div>`;

    const notificationContentConfig: INotificationHTML = {
      type: NotificationContentType.HTML,
      htmlString: `${widgetTitle} ${widgetAvailableContent} ${widgetFooter}`,
    };

    return {
      id,
      isNeedToRemoveOnPageLeave: true,
      type: NotificationType.INFO,
      content: [notificationContentConfig],
      closeNotification: async () => {
        this.uiActions.enableLoader();
        try {
          await this.saveWidgetConfig();
        } catch (error) {
        } finally {
          this.uiActions.disableLoader();
        }
      },
    };
  }

  public async saveWidgetConfig(): Promise<void> {
    const acknowledgedWidgetKeys = this.defaultItemKeys.filter((key) =>
      this.totalModules[key].permissionChecker(this.permissionService),
    );
    const widgetsConfig = this.stateService.getUserConfig(
      USER_CONFIG_MODULE_NAME.DASHBOARD_WIDGETS,
      USER_CONFIG_MODULE_TYPE.DASHBOARD,
    );
    const newWidgetsConfig = {
      ...widgetsConfig?.module_config,
      confirmedWidgets: acknowledgedWidgetKeys,
    };
    await this.toolsService
      .saveUserConfig(
        USER_CONFIG_MODULE_TYPE.DASHBOARD,
        USER_CONFIG_MODULE_NAME.DASHBOARD_WIDGETS,
        newWidgetsConfig,
      )
      .toPromise();

    this.uiActions.updateUserConfig([
      {
        module_config: newWidgetsConfig,
        module_type: USER_CONFIG_MODULE_TYPE.DASHBOARD,
        module_name: USER_CONFIG_MODULE_NAME.DASHBOARD_WIDGETS,
      },
    ]);
    this.newWidgets = [];
    this.isNewWidgetReminderInitialized = false;
    this.uiActions.removeNotification(this.notificationId);
    this.notificationId = null;
  }
}
